import type { FileWithId } from "src/types";

import { memo, useCallback } from "react";

import Button from "@mui/material/Button";

import { PipelineType } from "src/hooks/api/pipeline/useCreateRun"
import { useCreateIntent } from "src/hooks/api/intent/useCreateIntent";

import { AUDIO_FILE_FORMATS } from "src/utils/constants";

import { useMessageContext } from "../../hooks/uesMessageContext";
import { useChatHistoryContext } from "../../hooks/useChatHistoryContext";

type FooterProps = {
    selectedRowsData: FileWithId[];
    onClose: () => void;
    onS3AudioFileUpload: (intendId: string, file: FileWithId) => void;
};

export const FooterFolder = memo(({
    selectedRowsData,
    onClose,
    onS3AudioFileUpload
}: FooterProps) => {
    const { history, allFiles, setAllFiles, setDocumentId, currentFileFormat, setIntentId } = useChatHistoryContext();
    const { message, setMessage, setIsTranscribing, maxFileSize, setMaxFileSize } = useMessageContext();
    const { createIntent } = useCreateIntent();

    const handleImport = useCallback(() => {
        // Get IDs of currently selected files
        const selectedFileIds = selectedRowsData.map(file => file.id);

        // Filter out files that are no longer selected
        const remainingFiles = allFiles.filter(file => selectedFileIds.includes(file.id) || !file?.isS3Upload);

        // Add new files that aren't already in allFiles in current seesion.
        const newFiles = selectedRowsData.filter(file =>
            !allFiles.some(existingFile => existingFile.id === file.id)
        );

        // Filter out files that are selected but not send to BE for interaction.
        const newMessageFile = message.files.filter(
            file =>
                selectedRowsData.some(sel => sel.id === file.id) &&
                file.isS3Upload
        );
        
        // Update allFiles with remaining + new files
        const updatedFiles = [...remainingFiles, ...newFiles];
        setAllFiles(updatedFiles);
        setMessage(prev => ({
            ...prev,
            files: prev.files.filter(file => !file.isS3Upload).concat(newMessageFile, newFiles)
        }));
        setDocumentId({
            documentId: [...(history.documentId ?? []), ...newFiles.map(file => file.id)],
        });
        if (AUDIO_FILE_FORMATS.includes(currentFileFormat || "")) {
            if (maxFileSize < newFiles[0].file.size) setMaxFileSize(newFiles[0].file.size);
            setIsTranscribing(true);
            if (history.intentId) {
                onS3AudioFileUpload(history.intentId, newFiles[0]);
            } else {
                createIntent(
                    {
                        title: 'Chat',
                        file_ids: [],
                        intent_metadata: {},
                        pipeline_type: PipelineType.NOT_DEFINED,
                        tags: ['ai_studio'],
                    },
                    {
                        onSuccess: (intentId) => {
                            setIntentId({ intentId });
                            onS3AudioFileUpload(intentId, newFiles[0]);
                        },
                    }
                );
            }
            return;
        }
        onClose();
    }, [
        selectedRowsData,
        allFiles,
        setAllFiles,
        setMessage,
        setDocumentId,
        history.documentId,
        history.intentId,
        message.files,
        currentFileFormat,
        onS3AudioFileUpload,
        createIntent,
        setIntentId,
        onClose,
        setIsTranscribing,
        maxFileSize,
        setMaxFileSize
    ]);

    return (
        <>
            <Button onClick={onClose} color="inherit">
                Cancel
            </Button>
            <Button
                variant="contained"
                color="primary"
                onClick={handleImport}
            >
                Import
            </Button>
        </>
    );
});